import "@fider/assets/styles/index.scss"
// Tùy biến KHCN: một bộ chữ sans duy nhất "Plus Jakarta Sans" (variable, đủ dấu tiếng Việt) cho giao diện
// lẫn phần đọc: gọn, hiện đại, đồng nhất. Tự host để hợp CSP font-src 'self'.
import "@fontsource-variable/plus-jakarta-sans"

import React, { Suspense, useEffect, useMemo, useState } from "react"
import { createRoot } from "react-dom/client"
import { ErrorBoundary, Loader, ReadOnlyNotice, DevBanner } from "@fider/components"
import { classSet, Fider, FiderContext, actions, activateI18N } from "@fider/services"

import { I18n } from "@lingui/core"
import { I18nProvider } from "@lingui/react"
import { AsyncPage } from "./AsyncPages"

const Loading = () => (
  <div className="page">
    <Loader />
  </div>
)

const logProductionError = (err: Error) => {
  if (Fider.isProduction()) {
    console.error(err)
    actions.logError(`react.ErrorBoundary: ${err.message}`, err)
  }
}

window.addEventListener("unhandledrejection", (evt: PromiseRejectionEvent) => {
  if (evt.reason instanceof Error) {
    actions.logError(`window.unhandledrejection: ${evt.reason.message}`, evt.reason)
  } else if (evt.reason) {
    actions.logError(`window.unhandledrejection: ${evt.reason.toString()}`)
  }
})

window.addEventListener("error", (evt: ErrorEvent) => {
  if (evt.error && evt.colno > 0 && evt.lineno > 0) {
    actions.logError(`window.error: ${evt.message}`, evt.error)
  }
})

// Tùy biến KHCN: đổi ngôn ngữ ngay tại client (không tải lại trang). Khi LocaleSwitcher gọi
// i18n.activate(), lingui phát sự kiện "change"; AppRoot đổi `locale` state và re-key trang để
// toàn bộ cây (kể cả các chuỗi dùng i18n._() theo lối mệnh lệnh) render lại theo ngôn ngữ mới.
const AppRoot = ({ i18n }: { i18n: I18n }) => {
  const [locale, setLocale] = useState(i18n.locale)
  // Giữ nguyên một lazy component để chỉ remount khi đổi ngôn ngữ (đổi key), không phải mỗi lần render.
  const component = useMemo(() => AsyncPage(fider.session.page), [])

  useEffect(() => {
    const unsubscribe = i18n.on("change", () => setLocale(i18n.locale))
    return unsubscribe
  }, [i18n])

  return (
    <React.StrictMode>
      <ErrorBoundary onError={logProductionError}>
        <I18nProvider i18n={i18n}>
          <FiderContext.Provider value={fider}>
            <DevBanner />
            <ReadOnlyNotice />
            <Suspense fallback={<Loading />}>{React.createElement(component, { key: locale, ...fider.session.props })}</Suspense>
          </FiderContext.Provider>
        </I18nProvider>
      </ErrorBoundary>
    </React.StrictMode>
  )
}

const bootstrapApp = (i18n: I18n) => {
  document.body.className = classSet({
    "is-authenticated": fider.session.isAuthenticated,
    "is-staff": fider.session.isAuthenticated && fider.session.user.isCollaborator,
  })

  const rootElement = document.getElementById("root")
  if (rootElement) {
    const root = createRoot(rootElement)
    root.render(<AppRoot i18n={i18n} />)
  }
}
const fider = Fider.initialize()
__webpack_nonce__ = fider.session.contextID
__webpack_public_path__ = `${fider.settings.assetsURL}/assets/`
activateI18N(fider.currentLocale).then(bootstrapApp).catch(bootstrapApp)
